import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Search, PenTool, Code2, Rocket } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const STEPS = [
  {
    title: 'Descoberta',
    desc: 'Entendemos o negócio, os usuários e o que gera valor de verdade. Saímos com escopo, prioridades e um plano claro.',
    items: ['Objetivos e métricas', 'Mapa de funcionalidades', 'Escopo e prioridades'],
    Icon: Search,
  },
  {
    title: 'Design',
    desc: 'Prototipamos a experiência antes da primeira linha de código, para alinhar visão e expectativa desde o início.',
    items: ['Fluxos e wireframes', 'Protótipo navegável', 'Interface final'],
    Icon: PenTool,
  },
  {
    title: 'Desenvolvimento',
    desc: 'Código limpo e escalável, em entregas contínuas que você acompanha e testa a cada etapa.',
    items: ['Entregas contínuas', 'Versões para teste', 'Código revisado'],
    Icon: Code2,
  },
  {
    title: 'Lançamento',
    desc: 'Publicamos nas lojas e na web, monitoramos e seguimos evoluindo o produto com dados reais.',
    items: ['App Store e Google Play', 'Monitoramento', 'Evolução com dados'],
    Icon: Rocket,
  },
];

/**
 * Etapas como cartas que grudam no topo e se empilham: a anterior recua
 * (escala e escurece) enquanto a próxima sobe por cima.
 */
function Process() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();
      // Cartas empilhando (desktop e celular).
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const cards = q('.hv3-step');
        cards.forEach((card, i) => {
          const next = cards[i + 1];
          if (!next) return;
          const el = card.querySelector('.hv3-step-card');
          // A carta só recua quando a próxima já está quase cobrindo ela:
          // enquanto está sendo lida, fica intacta.
          gsap.to(el, {
            scale: 0.94,
            ease: 'none',
            scrollTrigger: { trigger: next, start: 'top 55%', end: 'top 18%', scrub: true },
          });
          gsap.to(el, {
            filter: 'brightness(0.6)',
            ease: 'none',
            scrollTrigger: { trigger: next, start: 'top 35%', end: 'top 18%', scrub: true },
          });
        });
      });
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.from(q('.hv3-process-title'), {
          y: 40,
          autoAlpha: 0,
          duration: 1.1,
          ease: 'expo.out',
          scrollTrigger: { trigger: root.current, start: 'top 75%' },
        });
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section className="hv3-process" ref={root} aria-labelledby="processo-title">
      <div className="hv3-wrap">
        <h2 id="processo-title" className="hv3-h2 hv3-process-title">
          Sem caixa-preta. <span className="hv3-dim">Você acompanha cada etapa.</span>
        </h2>

        <ol className="hv3-steps">
          {STEPS.map((s, i) => (
            <li className="hv3-step" key={s.title} style={{ '--i': i }}>
              <div className="hv3-step-card">
                <span className="hv3-step-n" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="hv3-step-body">
                  <h3 className="hv3-step-title">{s.title}</h3>
                  <p className="hv3-step-desc">{s.desc}</p>
                </div>
                <ul className="hv3-step-items">
                  {s.items.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
                <span className="hv3-step-icon" aria-hidden>
                  <s.Icon size={40} strokeWidth={1.25} />
                </span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default Process;
